import { action, computed, observable } from 'mobx';
import { ClientExperienceMode } from '~/components/ClientExperience/GiftCard/types/client-exprience.types';
import { ICXCompanyConfiguration } from '~/types/cx-types/cx-company-configuration.types';
import {
    CXEnvironments,
    ICXEnvironment
} from '~/types/cx-types/cx-environment.types';
import { getQueryParam } from '~/utils/url.utils';
import { isDevelopment } from '~/utils/environment.utils';
import type { GlobalCXContextValue } from '~/components/ClientExperience/providers/global-context/global-cx.context';
import { getStagingApiBaseUrl } from '~/utils/cx-utils/cx-api.utils';

export class ClientPortalConfigStore {
    globalContext: GlobalCXContextValue = null;
    @observable env: ICXEnvironment = null;
    @observable configuration: ICXCompanyConfiguration = null;
    @observable isPreviewOldRevision = false;
    @observable mode: ClientExperienceMode = getQueryParam('previewMode')
        ? ClientExperienceMode.PreviewActive
        : ClientExperienceMode.Production;

    constructor(globalContext: GlobalCXContextValue) {
        this.globalContext = globalContext;
    }

    @computed
    get memberAccountId() {
        return this.configuration.account_id;
    }

    @computed
    get smsConsentDisabled() {
        return !!this?.configuration?.sms_consent_disabled;
    }

    @computed
    get isPreview() {
        return [
            ClientExperienceMode.PreviewDraft,
            ClientExperienceMode.PreviewActive,
            ClientExperienceMode.PreviewVersionHistory
        ].includes(this.mode);
    }

    @computed
    get brandColor() {
        return this.configuration?.brand?.brand_color;
    }

    @computed
    get isPreviewDraft() {
        return this.mode === ClientExperienceMode.PreviewDraft;
    }

    @computed
    get isPreviewActive() {
        return this.mode === ClientExperienceMode.PreviewActive;
    }

    @computed
    get isPreviewVersionHistory() {
        return this.mode === ClientExperienceMode.PreviewVersionHistory;
    }

    @computed get apiBaseUrl() {
        if (!this.env) {
            return null;
        }

        switch (this.env.environment) {
            case CXEnvironments.Production:
                return 'https://api.honeybook.com';
            case CXEnvironments.Development:
                return '';
            case CXEnvironments.Staging:
                return getStagingApiBaseUrl();
            default:
                // TODO: Is this needed after loading initial data from post message?
                return this.isPreview ? '' : null;
        }
    }

    @computed
    get s3PlacementFilesUrlPrefix() {
        let urlPrefix = isDevelopment()
            ? `local_machines_assets/${process.env.USER}`
            : 'assets_users_production';

        const { configuration, env } = this;

        if (
            env.environment === CXEnvironments.Staging &&
            configuration.staging_api_base_url
        ) {
            const hostname = new URL(
                configuration.staging_api_base_url
            ).hostname.split('.')[0];

            // TODO: how should this consider hb-mx-staging?
            const finalHostName =
                hostname === 'staging-qa-app' ? 'hb-app-staging-qa' : hostname;
            urlPrefix = `staging_assets/${finalHostName}`;

            // https://widget.honeybook.com/staging_assets/hb-app-staging-qa/websiteplacements/5d1b44f952bab400135d3591/user_config_5d1b44f952bab400135d3591.js
            // https://widget.honeybook.com/staging_assets/staging-qa-app/websiteplacements/5d1b44f952bab400135d3591/user_config_5d1b44f952bab400135d3591.js?xhr=1
        }

        return urlPrefix;
    }

    @action
    setClientPortalEnv(newClientPortalEnv: Partial<ICXEnvironment>) {
        this.env = Object.assign({}, this.env, newClientPortalEnv);
    }

    @action
    setClientPortalConfiguration(
        newClientPortalConfiguration: ICXCompanyConfiguration
    ) {
        this.configuration = Object.assign(
            this.configuration || {},
            newClientPortalConfiguration
        );
    }

    @action
    setMode(mode: ClientExperienceMode) {
        this.mode = mode;
    }

    @computed
    get clientPortalCompanyId() {
        return this.configuration?.company_id;
    }

    @computed
    get vendorCompany() {
        return this.globalContext.companyStore.byId(this.clientPortalCompanyId);
    }

    @action
    setIsPreviewOldRevision(value: boolean) {
        this.isPreviewOldRevision = value;
    }
}
